import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Validations from './Validations.js';
import Api from '../common/Api.js';
import jwt_encode from 'jwt-encode';
import jwt_decode from 'jwt-decode';
import { toast } from 'react-toastify';
import Select from 'react-select';

// Keeps react-select single-line + fixed height
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 44, height: 44 }),
  valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden' }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

export default function Register() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const [res_errors, setRes_errors] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Initial auth check + load categories & states
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (localStorage.getItem('franchisee-otp-token') !== null) {
      navigate('/franchisee-verifications');
      return;
    }
    if (localStorage.getItem('franchisee-token') !== null) {
      navigate('/franchisee-dashboard');
      return;
    }

    const fetchdata = async () => {
      try {
        const response = await Api.get('/franchisee/get_form_data');
        setCategories(response.data.categories || []);

        const stateList = (response.data.state_list || []).map((s) => ({
          value: s.value ?? s.code,
          label: s.label ?? s.name,
        }));
        setStates(stateList);
      } catch (err) {
        toast.error('Failed to load form data');
      }
    };

    fetchdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // State → load its cities
  // ---------------------------------------------------------------------------
  const handleState = async (selected) => {
    setSelectedState(selected);
    setSelectedCity(null);
    setCities([]);

    if (!selected) return;

    setLoadingCities(true);
    try {
      const { data } = await Api.get('/home/cities', {
        params: { code: selected.value },
      });
      setCities((data || []).map((c) => ({ value: c.name, label: c.name })));
    } catch (err) {
      toast.error('Failed to load cities');
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCity = (selected) => {
    setSelectedCity(selected);
  };

  // ---------------------------------------------------------------------------
  // Form fields + validation + submit
  // ---------------------------------------------------------------------------
  const fields = {
    shopName: '',
    category: '',
    mobile: '',
    password: '',
    ownerName: '',
    state: '',
    city: '',
    shopPincode: '',
    shopAddress: ''
  };

  const custRegister = async () => {
    // react-select values
    const stateVal = selectedState?.value || '';
    const cityVal = selectedCity?.value || '';

    // The hook doesn't know about these two, so validate them here
    if (!stateVal || !cityVal) {
      setRes_errors({
        ...res_errors,
        ...(stateVal ? {} : { state: 'Please select a state.' }),
        ...(cityVal ? {} : { city: 'Please select a city.' }),
      });
      return;               // stop before the API call
    }

    setRes_errors({});
    setLoading(true);

    const payload = { ...values, state: stateVal, city: cityVal };

    try {
      const res = await Api.post(
        '/franchisee/signup',
        jwt_encode(payload, process.env.REACT_APP_SECRET_KEY)
      );
      const result = jwt_decode(res.data);
      console.log(result);
      const status = result.data.status;
      setLoading(false);

      if (status === 'failure') {
        const server_errors = result.data.errors || {};
        if (server_errors.mobile_exit === true) {
          toast.error(server_errors.mobile, {
            toastId: 'errorTranscation1',
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
        setRes_errors(server_errors);
      } else if (status === 'success') {
        localStorage.setItem('franchisee-otp-token', JSON.stringify(result.data.data));
        navigate('/franchisee-verifications');
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const { values, errors, handleChange, handleSubmit } = Validations(
    fields,
    custRegister
  );

  // Small helper to keep error rows compact
  const err = (clientErr, serverErr) =>
    clientErr ? clientErr : serverErr ? serverErr : '\u00A0';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* ---------------------------------------------------------------
          Scoped styles — apply only while THIS page is mounted.
          The shared style-freedom.css is NOT touched.
      ---------------------------------------------------------------- */}
      <style>{`
        .franchisee-register-page .forms23-grids {
          grid-template-columns: 1.35fr 1fr;
          grid-gap: 40px;
          align-items: center;
          padding-top: 3em;
        }

        .franchisee-register-page .form23 {
          padding: 32px 36px;
          border-radius: 6px;
        }

        .franchisee-register-page .form23 h6 {
          margin-bottom: 22px;
          font-size: 22px;
        }

        /* 2-column form grid */
        .franchisee-register-page .form23 form {
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 16px;
          row-gap: 0;
        }

        .franchisee-register-page .form23 form .fg {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .franchisee-register-page .form23 form .fg-full {
          grid-column: 1 / -1;
        }

        .franchisee-register-page .form23 form .fg-actions {
          margin-top: 1px;
        }

        /* Uniform control height for native inputs AND react-select */
        .franchisee-register-page .form23 .input-form,
        .franchisee-register-page .form23 .select__control {
          height: 44px;
          min-height: 44px;
          box-sizing: border-box;
          padding: 0 14px;
          font-size: 15px;
          color: #333;
          border: 1px solid #e2e2e2;
          border-radius: 4px;
          background: #fafafa;
          width: 100%;
          margin-bottom: 2px;
        }

        .franchisee-register-page .form23 .input-form:focus,
        .franchisee-register-page .form23 .select__control--is-focused {
          border-color: #f85c70;
          background: #fff;
        }

        .franchisee-register-page .form23 textarea.input-form {
          height: auto;
          min-height: 88px;
          padding: 12px 14px;
        }

        /* Tight, fixed-height error rows so columns stay aligned */
        .franchisee-register-page .form23 .text-danger {
          display: block;
          min-height: 14px;
          line-height: 14px;
          font-size: 12px;
          margin: 0 0 2px;
        }

        .franchisee-register-page .form23 form > p {
          margin: 10px 0 4px;
          font-size: 14px;
        }

        .franchisee-register-page .form23 button.button-eff {
          width: 100%;
          margin-top: 8px;
        }

        /* Right text panel — sized to stay readable next to denser form */
        .franchisee-register-page .form23-text h3 {
          font-size: 28px;
          line-height: 34px;
          margin-bottom: 14px;
        }

        .franchisee-register-page .form23-text p {
          font-size: 15px;
          line-height: 22px;
          margin-bottom: 22px;
        }

        /* Stack to one column on tablets/mobile */
        @media (max-width: 900px) {
          .franchisee-register-page .forms23-grids {
            grid-template-columns: 1fr;
            grid-gap: 30px;
            padding-top: 2em;
          }
          .franchisee-register-page .form23-text {
            text-align: center;
            max-width: 560px;
            margin: 0 auto;
          }
        }

        @media (max-width: 640px) {
          .franchisee-register-page .form23 {
            padding: 24px 20px;
          }
          .franchisee-register-page .form23 form {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="w3l-forms-23 franchisee-register-page">
        <div className="forms23-block">
          <div className="wrapper">
            <div className="d-grid forms23-grids">
              <div className="form23">
                <h6>Register your Shop</h6>

                <form onSubmit={handleSubmit} autoComplete="off">
                  {/* ---------- Row 1: Shop Name | Category ---------- */}
                  <div className="fg fg-full">
                    <input
                      type="text"
                      name="shopName"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Franchisee/Shop Name"
                      autoComplete="off"
                      value={values.shopName}
                    />
                    <small className="text-danger">{err(errors.shopName, res_errors.shopName)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  {/* ---------- Row 2: category | State ---------- */}

                  <div className="fg">
                    <select
                      className="input-form"
                      name="category"
                      onChange={handleChange}
                      value={values.category}
                    >
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <small className="text-danger">{err(errors.category, res_errors.category)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  <div className="fg">
                    <Select
                      name="state"
                      className="basic-single"
                      classNamePrefix="select"
                      styles={selectStyles}
                      value={selectedState}
                      onChange={handleState}
                      options={states}
                      placeholder="Select State"
                      isClearable
                      isSearchable
                    />
                    <small className="text-danger">{err(errors.state, res_errors.state)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  {/* ---------- Row 3: City | Pincode ---------- */}

                  <div className="fg">
                    <Select
                      name="city"
                      className="basic-single"
                      classNamePrefix="select"
                      styles={selectStyles}
                      value={selectedCity}
                      onChange={handleCity}
                      options={cities}
                      isLoading={loadingCities}
                      isDisabled={!selectedState}
                      placeholder={
                        selectedState ? 'Select City' : 'Select state first'
                      }
                      isClearable
                      isSearchable
                    />
                    <small className="text-danger">{err(errors.city, res_errors.city)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  <div className="fg">
                    <input
                      type="text"
                      name="shopPincode"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Shop Pincode"
                      autoComplete="off"
                      value={values.shopPincode}
                    />
                    <small className="text-danger">{err(errors.shopPincode, res_errors.shopPincode)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  {/* ---------- Row 4: Owner Name | (Mobile ---------- */}

                  <div className="fg">
                    <input
                      type="text"
                      name="ownerName"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Owner Name"
                      autoComplete="off"
                      value={values.ownerName}
                    />
                    <small className="text-danger">{err(errors.ownerName, res_errors.ownerName)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  <div className="fg">
                    <input
                      type="text"
                      name="mobile"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Mobile No (10 digit only)"
                      autoComplete="off"
                      value={values.mobile}
                    />
                    <small className="text-danger">{err(errors.mobile, res_errors.mobile)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  {/* ---------- Row 3: Password | Confirm Password ---------- */}

                  <div className="fg">
                    <input
                      type="password"
                      name="password"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Password"
                      autoComplete="off"
                      value={values.password}
                    />
                    <small className="text-danger">{err(errors.password, res_errors.password)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  <div className="fg">
                    <input
                      type="text"
                      name="confirmPassword"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Confirm Password"
                      autoComplete="off"
                      value={values.confPass}
                    />
                    <small className="text-danger">{err(errors.confPass, res_errors.confPass)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  {/* Empty grid slot to fill the row */}
                  <div className="fg" aria-hidden="true" />

                  {/* ---------- Full width: Address ---------- */}
                  <div className="fg fg-full">
                    <textarea
                      name="shopAddress"
                      className="input-form"
                      onChange={handleChange}
                      placeholder="Shop Address"
                      autoComplete="off"
                      value={values.shopAddress}
                    ></textarea>
                    <small className="text-danger">{err(errors.shopAddress, res_errors.shopAddress)}</small>
                    <small className="text-danger">{'\u00A0'}</small>
                  </div>

                  {/* ---------- Full width: Terms + Submit ---------- */}
                  <div className="fg fg-full fg-actions">
                    <p>
                      By signing up, you agree to our{' '}
                      <Link to="/terms-of-services">Terms of Use</Link>
                    </p>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn button-eff"
                      style={{ padding: 'unset', height: '44px' }}
                    >
                      <span style={{ marginLeft: '35px' }}>Submit</span>
                      <span
                        style={{
                          float: 'right',
                          width: '10px',
                          height: '10px',
                          marginLeft: '5px',
                        }}
                      >
                        {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                      </span>
                    </button>
                  </div>
                </form>

                <p>
                  Already have an account?{' '}
                  <Link to="/franchisee-login">Login</Link>
                </p>
              </div>

              <div className="form23-text">
                <h3 style={{ fontSize: "40px" }}>DLMP21</h3>
                <h3>
                  Shop Local. Join the Network. Earn Rewards.
                </h3>

                <p>
                  Discover local businesses, shop through the DLMP21 network,
                  and participate in a customer reward ecosystem built around
                  your everyday purchases.
                </p>

                <p>
                  <strong>
                    Register → Shop ₹1,000+ → Show Your DLMP Code →
                    Track Your Rewards
                  </strong>
                </p>
                <Link to="/" className="btn button-eff button-eff-2">
                  <span className="fa fa-hand-o-left" aria-hidden="true"></span>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}